/**
 * Wave 10 zod-mandate — runtime schema for {@link SyncQueueConfig}.
 *
 * `store` / `fetchImpl` / `serializer` / `onProgress` / `onConflict` /
 * `conflictPolicy` (function form) are callable/object handles — not
 * serialisable. They are validated as `z.unknown().optional()`/`z.any()` at
 * the boundary (type-checked only) — same convention as web-survey/wizard
 * for callbacks.
 *
 * @example
 * ```ts
 * import { SyncQueueConfigSchema } from './SyncQueueConfig.schema'
 * SyncQueueConfigSchema.parse({ endpoint: '/api/sync', store: anyStore })
 * ```
 */
import { z } from 'zod'

export const ConflictPolicyNameSchema = z.enum([
	'server-authoritative',
	'client-wins',
	'lww',
	'manual',
])

// Callbacks / handles — pass-through, type-checked only.
const callable = z.unknown()

export const SyncQueueConfigSchema = z
	.object({
		endpoint: z.string().min(1),
		store: z.unknown(),
		outboxTable: z.string().min(1).optional(),
		batchSize: z.number().int().positive().optional(),
		conflictPolicy: z.union([ConflictPolicyNameSchema, callable]).optional(),
		fetchImpl: callable.optional(),
		transport: callable.optional(),
		serializer: z.unknown().optional(),
		onProgress: callable.optional(),
		onConfirmation: callable.optional(),
		onConflict: callable.optional(),
		maxAttempts: z.number().int().nonnegative().optional(),
		backoffBaseMs: z.number().nonnegative().optional(),
		backoffMaxMs: z.number().nonnegative().optional(),
		heartbeatUrl: z.string().min(1).optional(),
		heartbeatIntervalMs: z.number().nonnegative().optional(),
		pollIntervalMs: z.number().nonnegative().optional(),
		serviceWorkerUrl: z.string().min(1).optional(),
		backgroundSyncTag: z.string().min(1).optional(),
		headers: z.record(z.string()).optional(),
	})
	.strict()

export type SyncQueueConfigInput = z.infer<typeof SyncQueueConfigSchema>
export type ConflictPolicyNameInput = z.infer<typeof ConflictPolicyNameSchema>
